import { useMemo, useState } from 'react';
import type { ScrapeRunDetail } from '../../../types/scraping';
import { AdminPanel } from '../AdminPage';
import { describeRun, formatDuration } from './runDiagnostics';
import './ScrapeRunInspector.css';

interface ScrapeRunInspectorProps {
  run: ScrapeRunDetail;
}

type PanelTab = 'text' | 'html' | 'reply' | 'config';

/**
 * Shows one run's extracted JSON alongside the page content it came from.
 *
 * The side-by-side layout is the point of the feature, not a nicety: a developer looking at an empty
 * result needs to know whether the page failed to render or the prompt failed to read it, and the only
 * way to tell is to see both at once. The verdict banner states the conclusion; the panels are the
 * evidence for it.
 */
export const ScrapeRunInspector = ({ run }: ScrapeRunInspectorProps) => {
  const [tab, setTab] = useState<PanelTab>('text');

  const diagnosis = useMemo(() => describeRun(run), [run]);

  const prettyJson = useMemo(() => formatJson(run.extractedJson), [run.extractedJson]);
  const prettyConfig = useMemo(() => formatJson(run.fetchConfigSnapshot), [run.fetchConfigSnapshot]);

  return (
    <AdminPanel className="scrape-run">
      <div className={`scrape-run__verdict scrape-run__verdict--${diagnosis.tone}`}>
        <div className="scrape-run__verdict-headline">{diagnosis.headline}</div>
        <p className="scrape-run__verdict-detail">{diagnosis.detail}</p>
        {diagnosis.nextStep && (
          <p className="scrape-run__verdict-next">
            <strong>Try:</strong> {diagnosis.nextStep}
          </p>
        )}
      </div>

      <dl className="scrape-run__facts">
        <Fact label="Status" value={run.status} />
        <Fact
          label="Failed at"
          value={run.failureStage === 'None' ? '—' : run.failureStage}
        />
        <Fact label="HTTP" value={run.httpStatus === null ? '—' : String(run.httpStatus)} />
        <Fact label="Pages" value={String(run.pagesRendered)} />
        <Fact label="Render" value={formatDuration(run.renderMs)} />
        <Fact label="Extract" value={formatDuration(run.extractionMs)} />
        <Fact label="Tokens" value={run.tokensUsed === null ? '—' : String(run.tokensUsed)} />
        <Fact label="Model" value={run.llmModel ?? '—'} />
      </dl>

      <div className="scrape-run__robots">
        <span className={`scrape-run__robots-badge scrape-run__robots-badge--${run.robotsStatus.toLowerCase()}`}>
          robots.txt: {run.robotsStatus}
        </span>
        {run.robotsDetail && <span className="scrape-run__robots-detail">{run.robotsDetail}</span>}
        {run.robotsStatus === 'Disallowed' && (
          // Stated plainly rather than buried: the tool proceeded anyway, and whoever is reading this
          // should know that was a deliberate policy choice and not an oversight.
          <span className="scrape-run__robots-note">
            This run proceeded anyway — robots is recorded, not enforced, for this admin-only tool.
          </span>
        )}
      </div>

      {run.pageTitle && (
        <div className="scrape-run__page-title">
          Page title: <span>{run.pageTitle}</span>
        </div>
      )}

      <div className="scrape-run__panes">
        <section className="scrape-run__pane">
          <h3 className="scrape-run__pane-title">Extracted JSON</h3>
          {prettyJson ? (
            <pre className="scrape-run__code">{prettyJson}</pre>
          ) : (
            <p className="scrape-run__empty">
              No valid JSON was extracted.
              {run.failureStage === 'Fetch'
                ? ' The page never rendered, so the prompt was not run.'
                : ' Check the model reply tab to see what came back instead.'}
            </p>
          )}
        </section>

        <section className="scrape-run__pane">
          <div className="scrape-run__pane-header">
            <h3 className="scrape-run__pane-title">What the page gave us</h3>
            <div className="scrape-run__tabs" role="tablist" aria-label="Raw run content">
              <TabButton current={tab} value="text" onSelect={setTab}>
                Page text
              </TabButton>
              <TabButton current={tab} value="html" onSelect={setTab}>
                Raw HTML
              </TabButton>
              <TabButton current={tab} value="reply" onSelect={setTab}>
                Model reply
              </TabButton>
              <TabButton current={tab} value="config" onSelect={setTab}>
                Config used
              </TabButton>
            </div>
          </div>

          {tab === 'text' && (
            <RawPanel
              value={run.renderedText}
              pruned={run.rawContentPruned}
              emptyLabel="No page text was captured."
              hint="This is the exact text handed to the model."
            />
          )}
          {tab === 'html' && (
            <RawPanel
              value={run.rawHtml}
              pruned={run.rawContentPruned}
              emptyLabel="No HTML was captured."
              hint="Read this to tell a hydrated page from an app shell."
            />
          )}
          {tab === 'reply' && (
            <RawPanel
              value={run.rawLlmResponse}
              pruned={run.rawContentPruned}
              emptyLabel="The model was never called."
              hint="The reply verbatim, before JSON parsing."
            />
          )}
          {tab === 'config' && (
            <>
              <p className="scrape-run__hint">
                The fetch settings and prompt as they were for this run — so comparing two runs shows
                what actually changed.
              </p>
              <pre className="scrape-run__code">{prettyConfig ?? '—'}</pre>
              <pre className="scrape-run__code">{run.extractionPromptSnapshot ?? '—'}</pre>
            </>
          )}
        </section>
      </div>
    </AdminPanel>
  );
};

const Fact = ({ label, value }: { label: string; value: string }) => (
  <div className="scrape-run__fact">
    <dt>{label}</dt>
    <dd>{value}</dd>
  </div>
);

interface TabButtonProps {
  current: PanelTab;
  value: PanelTab;
  onSelect: (tab: PanelTab) => void;
  children: React.ReactNode;
}

const TabButton = ({ current, value, onSelect, children }: TabButtonProps) => (
  <button
    type="button"
    role="tab"
    aria-selected={current === value}
    className={`scrape-run__tab ${current === value ? 'scrape-run__tab--active' : ''}`}
    onClick={() => onSelect(value)}
  >
    {children}
  </button>
);

interface RawPanelProps {
  value: string | null;
  pruned: boolean;
  emptyLabel: string;
  hint: string;
}

/**
 * Renders one raw payload, distinguishing "pruned by retention" from "never captured". Without that
 * distinction an old run looks identical to a failed render, which would make the history misleading
 * exactly where it is supposed to be useful.
 */
const RawPanel = ({ value, pruned, emptyLabel, hint }: RawPanelProps) => {
  if (value) {
    return (
      <>
        <p className="scrape-run__hint">{hint}</p>
        <pre className="scrape-run__code">{value}</pre>
      </>
    );
  }

  return (
    <p className="scrape-run__empty">
      {pruned
        ? 'The raw content for this run has been pruned to save space. Its extracted result and timings are still above; re-run the profile to capture fresh content.'
        : emptyLabel}
    </p>
  );
};

/** Pretty-prints a JSON string for display, falling back to the raw text when it will not parse. */
const formatJson = (raw: string | null): string | null => {
  if (!raw) return null;
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};
